import React from "react";
import { X, Trash2, AlertTriangle } from "lucide-react";

// Branded confirm modal — replaces the browser-native window.confirm() for
// destructive actions. Same top-bar / coral-underline treatment we use
// everywhere else (see SetGoToScenarioModal). Reusable — see the
// `variant` prop for the primary button color/icon.
//
// Props:
//   title       — small uppercase eyebrow ("Delete Scenario")
//   message     — main copy (accepts a ReactNode so callers can bold names)
//   detail      — optional pre-formatted callout box (e.g. the row name)
//   confirmLabel — primary button text ("Delete", "Publish", …)
//   variant     — "danger" (coral primary) | "primary" (navy primary)
//   onCancel    — closes the modal without action
//   onConfirm   — invoked when primary button clicked (may return a Promise)
//   busy        — if truthy, primary button is disabled + shows a subtle
//                 loading state so double-clicks don't fire twice.
export default function ConfirmActionModal({
  title = "Confirm",
  message,
  detail,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  variant = "danger",
  onCancel,
  onConfirm,
  busy = false,
}) {
  const isDanger = variant === "danger";
  const primaryStyle = isDanger
    ? { background: "#EA6A47", color: "#fff" }
    : { background: "#1C4E80", color: "#fff" };
  const Icon = isDanger ? Trash2 : AlertTriangle;

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center"
      style={{ background: "rgba(15,42,68,0.55)" }}
      onClick={onCancel}
      data-testid="confirm-action-modal"
    >
      <div
        className="bg-white border-2 border-navy shadow-2xl w-[440px] max-w-[95vw] rounded-[8px]"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="flex items-center justify-between px-4 py-2.5 border-b-2"
          style={{ background: "var(--u-navy-wash)", borderBottomColor: "#EA6A47" }}
        >
          <div className="flex items-center gap-1.5">
            <AlertTriangle size={13} className={isDanger ? "text-coral" : "text-navy"} />
            <div className="text-[12px] tracking-[0.14em] uppercase font-bold text-navy">
              {title}
            </div>
          </div>
          <button
            onClick={onCancel}
            className="text-slate hover:text-coral"
            data-testid="confirm-action-close"
          >
            <X size={14} />
          </button>
        </div>

        <div className="px-5 py-4 text-[12px] text-ink leading-relaxed">
          <div className="mb-2">{message}</div>
          {detail && (
            <div
              className="border border-rule bg-[#F5F8FB] px-3 py-2 mt-2 font-mono text-[11px] text-navy break-words"
              data-testid="confirm-action-detail"
            >
              {detail}
            </div>
          )}
        </div>

        <div
          className="flex items-center justify-end gap-2 px-4 py-3 border-t border-rule"
          style={{ background: "#FAFBFC" }}
        >
          <button
            onClick={onCancel}
            disabled={busy}
            data-testid="confirm-action-cancel"
            className="border border-rule bg-white hover:border-navy hover:text-navy px-3 py-1.5 text-[11px] font-semibold text-slate disabled:opacity-40"
          >
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            data-testid="confirm-action-primary"
            style={primaryStyle}
            className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold disabled:opacity-40 hover:opacity-90"
          >
            <Icon size={12} />
            {busy ? "Working…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
